import React, { useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Fade,
  LinearProgress,
} from "@mui/material";
import {
  MyLocation as GpsIcon,
  Storefront as ShopIcon,
} from "@mui/icons-material";

const containerStyle = {
  width: "100%",
  height: "400px",
  borderRadius: "16px",
  marginTop: "16px",
};

const Location = () => {
  const API_KEY = "AIzaSyAawfaCQ57puQnjtjON7WzoxW9GUsT4oCs";
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
  });

  const [step, setStep] = useState(1);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [countdown, setCountdown] = useState(10); // Vraćeno na 10 sekundi
  const [coords, setCoords] = useState({ lat: 44.2064, lng: 17.9077 });
  const [detectedCity, setDetectedCity] = useState("");
  const [selectedMarker, setSelectedMarker] = useState(null);

  const [objekti, setObjekti] = useState([
    {
      id: 1,
      name: "Poslovnica Ferhadija",
      city: "Sarajevo",
      lat: 43.8587,
      lng: 18.4288,
      status: "Povezan",
      type: "Maloprodaja",
    },
    {
      id: 2,
      name: "Glavno Skladište Stup",
      city: "Sarajevo",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Skladište",
    },
    {
      id: 3,
      name: "Poslovnica Ilidža",
      city: "Sarajevo",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Maloprodaja",
    },
    {
      id: 4,
      name: "Regionalni Ured BL",
      city: "Banja Luka",
      lat: 44.7722,
      lng: 17.191,
      status: "Povezan",
      type: "Ured",
    },
    {
      id: 5,
      name: "Distributivni Centar Lazarevo",
      city: "Banja Luka",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Skladište",
    },
    {
      id: 6,
      name: "Poslovnica Rondo",
      city: "Mostar",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Maloprodaja",
    },
    {
      id: 7,
      name: "Poslovnica Sjever",
      city: "Tuzla",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Maloprodaja",
    },
    {
      id: 8,
      name: "Centralno Skladište Grude",
      city: "Grude",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Skladište",
    },
    {
      id: 9,
      name: "Poslovnica Boboška",
      city: "Grude",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Maloprodaja",
    },
    {
      id: 11,
      name: "Caffe Azur",
      city: "Široki Brijeg",
      lat: null,
      lng: null,
      status: "Nepovezan",
      type: "Caffe Bar",
    },
  ]);

  const normalizeText = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "d")
      .replace("grad ", "")
      .replace("opcina ", "")
      .replace("opstina ", "")
      .trim();
  };

  const startLocationScan = () => {
    // ODMAH PITA KORISNIKA ZA LOKACIJU
    if (!navigator.geolocation) {
      alert("Vaš preglednik ne podržava GPS lokaciju.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Ako je lokacija uspješno dobivena, kreće odbrojavanje
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setCoords({ lat, lng });

        setStep(2);
        setLoadingProgress(0);
        setCountdown(10);

        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              setTimeout(() => executeGpsSearch(lat, lng), 600);
              return 0;
            }
            return prev - 1;
          });
          setLoadingProgress((prev) => prev + 10);
        }, 1000);
      },
      (error) => {
        // ODMAH IZBACUJE GREŠKU AKO GPS NIJE DOSTUPAN
        if (error.code === error.PERMISSION_DENIED) {
          alert(
            "Pristup lokaciji je odbijen. Molimo dopustite lokaciju u postavkama preglednika i uključite GPS na mobitelu.",
          );
        } else {
          alert(
            "Nije moguće dohvatiti lokaciju. Provjerite je li GPS uključen.",
          );
        }
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const executeGpsSearch = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        let city = "Nepoznato";
        for (let res of results) {
          for (let comp of res.address_components) {
            if (
              comp.types.includes("locality") ||
              comp.types.includes("administrative_area_level_2")
            ) {
              city = comp.long_name
                .replace("Grad ", "")
                .replace("Općina ", "")
                .replace("Opština ", "")
                .trim();
              break;
            }
          }
          if (city !== "Nepoznato") break;
        }
        setDetectedCity(city);
        setStep(3);
      } else {
        setStep(1);
      }
    });
  };

  const relevantObjects = objekti.filter((obj) => {
    const dbCity = normalizeText(obj.city);
    const gpsCity = normalizeText(detectedCity);
    return (
      (dbCity.includes(gpsCity) || gpsCity.includes(dbCity)) && obj.lat === null
    );
  });

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", p: { xs: 1, md: 3 } }}>
      {step === 1 && (
        <Fade in>
          <Stack spacing={2}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: 4,
                bgcolor: "white",
              }}
            >
              <GpsIcon sx={{ fontSize: 60, color: "#284c7a", mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                BiH Terenski CRM
              </Typography>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={startLocationScan}
                sx={{
                  mt: 2,
                  bgcolor: "#284c7a",
                  py: 2,
                  fontWeight: 700,
                  borderRadius: 3,
                }}
              >
                ZAPOČNI SKENIRANJE
              </Button>
            </Paper>

            <Paper
              elevation={2}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                border: "1px solid #ddd",
              }}
            >
              {isLoaded && (
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={coords}
                  zoom={7}
                  options={{
                    mapTypeControl: true,
                    zoomControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                  }}
                >
                  {objekti.map(
                    (obj) =>
                      obj.lat && (
                        <Marker
                          key={obj.id}
                          position={{ lat: obj.lat, lng: obj.lng }}
                          onClick={() => setSelectedMarker(obj)}
                        />
                      ),
                  )}
                  {selectedMarker && (
                    <InfoWindow
                      position={{
                        lat: selectedMarker.lat,
                        lng: selectedMarker.lng,
                      }}
                      onCloseClick={() => setSelectedMarker(null)}
                    >
                      <Box sx={{ p: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800 }}
                        >
                          {selectedMarker.name}
                        </Typography>
                        <Typography variant="caption">
                          {selectedMarker.city} - {selectedMarker.type}
                        </Typography>
                      </Box>
                    </InfoWindow>
                  )}
                </GoogleMap>
              )}
            </Paper>
          </Stack>
        </Fade>
      )}

      {step === 2 && (
        <Box sx={{ textAlign: "center", mt: 10, px: 2 }}>
          <CircularProgress
            size={80}
            thickness={2}
            sx={{ color: "#284c7a", mb: 4 }}
          />
          <Typography variant="h3" sx={{ fontWeight: 900, color: "#284c7a" }}>
            {countdown}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            Kalibracija GPS preciznosti...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={loadingProgress}
            sx={{
              mx: 4,
              mt: 4,
              height: 12,
              borderRadius: 6,
              bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": { bgcolor: "#284c7a" },
            }}
          />
        </Box>
      )}

      {step === 3 && (
        <Fade in>
          <Box sx={{ px: 1 }}>
            <Paper
              sx={{
                p: 3,
                mb: 3,
                bgcolor: "#f0f7ff",
                border: "1px solid #c2e0ff",
                borderRadius: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: "#004799" }}
              >
                📍 Pronađeno: {detectedCity}
              </Typography>
            </Paper>
            <List>
              {relevantObjects.length > 0 ? (
                relevantObjects.map((obj) => (
                  <Paper
                    key={obj.id}
                    sx={{
                      mb: 2,
                      border: "2px solid #e2e8f0",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <ListItemButton
                      sx={{
                        p: 3,
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                      onClick={() => {
                        setObjekti((prev) =>
                          prev.map((o) =>
                            o.id === obj.id
                              ? {
                                  ...o,
                                  lat: coords.lat,
                                  lng: coords.lng,
                                  status: "Povezan",
                                }
                              : o,
                          ),
                        );
                        setStep(1);
                        alert("Uspješno povezano!");
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {obj.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {obj.type} - {obj.city}
                      </Typography>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<GpsIcon />}
                        sx={{
                          bgcolor: "#284c7a",
                          color: "white",
                          fontWeight: 800,
                          py: 1.5,
                          borderRadius: 2,
                        }}
                      >
                        POVEŽI SA OVOM LOKACIJOM
                      </Button>
                    </ListItemButton>
                  </Paper>
                ))
              ) : (
                <Typography
                  sx={{
                    p: 4,
                    textAlign: "center",
                    color: "orange",
                    fontWeight: 700,
                  }}
                >
                  Nema nepovezanih objekata za grad {detectedCity}.
                </Typography>
              )}
              <Button
                fullWidth
                onClick={() => setStep(1)}
                sx={{ mt: 2, py: 2, fontWeight: 700, color: "#64748b" }}
              >
                ODUSTANI
              </Button>
            </List>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default Location;
